export const WHATSAPP_FLOW_NODE_TYPE = "whatsapp_flow";
export const WHATSAPP_FLOW_OUTPUTS = Object.freeze([
  { id: "submitted", label: "Submitted" },
  { id: "send_failed", label: "Send Failed" },
  { id: "timed_out", label: "Timed Out" },
  { id: "processing_failed", label: "Processing Failed" },
]);
export const WHATSAPP_FLOW_TIMEOUT_MINUTES = 5;
export const WHATSAPP_FLOW_TIMEOUT_MAXIMUM_MINUTES = 10080;

export const createWhatsAppFlowConfig = () => ({
  dataChannelMode: "Endpointless",
  flowDefinitionId: "",
  flowRevisionId: "",
  entry: {
    action: "Navigate",
    bodyText: "Please share a few details so I can assist you better.",
    buttonText: "Share Details",
    startScreenId: null,
  },
  fieldMappings: [],
  initialDataBindings: [],
  timeoutPolicy: { timeoutMinutes: 1440 },
});

const compatibleDestinationTypes = {
  text: ["text"],
  email: ["text"],
  phone: ["text"],
  integer: ["number"],
  decimal: ["number"],
  date: ["date"],
  boolean: ["boolean"],
  json: [],
};

export const WHATSAPP_FLOW_DESTINATION_KINDS = Object.freeze({
  CONTACT_CUSTOM_FIELD: "ContactCustomField",
  REQUEST_CONTEXT: "RequestContext",
});

const normalizeTypeName = value => String(value || "").trim().toLowerCase();

export const isWhatsAppFlowFieldCompatible = (
  responseType,
  destinationType,
  destinationKind = WHATSAPP_FLOW_DESTINATION_KINDS.CONTACT_CUSTOM_FIELD
) => {
  const response = normalizeTypeName(responseType);
  const destination = normalizeTypeName(destinationType);
  const requestContext =
    destinationKind === WHATSAPP_FLOW_DESTINATION_KINDS.REQUEST_CONTEXT;
  if (
    requestContext &&
    ["text", "email", "phone"].includes(response) &&
    destination === "singleselect"
  )
    return true;
  if (requestContext && response === "json" && destination === "multiselect")
    return true;
  return (compatibleDestinationTypes[response] || []).includes(destination);
};

export const normalizeWhatsAppFlowConfig = config => ({
  dataChannelMode: "Endpointless",
  flowDefinitionId: String(config?.flowDefinitionId || "").trim(),
  flowRevisionId: String(config?.flowRevisionId || "").trim(),
  entry: {
    action: "Navigate",
    bodyText: String(config?.entry?.bodyText || "").trim(),
    buttonText: String(config?.entry?.buttonText || "").trim(),
    startScreenId: null,
  },
  fieldMappings: (Array.isArray(config?.fieldMappings)
    ? config.fieldMappings
    : []
  ).map(mapping => ({
    responseKey: String(mapping?.responseKey || "").trim(),
    responseType: String(mapping?.responseType || "").trim(),
    fieldId: String(mapping?.fieldId || "").trim(),
    destinationKind:
      mapping?.destinationKind === WHATSAPP_FLOW_DESTINATION_KINDS.REQUEST_CONTEXT
        ? WHATSAPP_FLOW_DESTINATION_KINDS.REQUEST_CONTEXT
        : WHATSAPP_FLOW_DESTINATION_KINDS.CONTACT_CUSTOM_FIELD,
    required: mapping?.required === true,
  })),
  initialDataBindings: (Array.isArray(config?.initialDataBindings)
    ? config.initialDataBindings
    : []
  ).map(binding => ({
    dataKey: String(binding?.dataKey || "").trim().toLowerCase(),
    sourceKind: String(binding?.sourceKind || "").trim(),
    sourceFieldKey: binding?.sourceFieldKey
      ? String(binding.sourceFieldKey).trim()
      : null,
    sourceFieldId: binding?.sourceFieldId
      ? String(binding.sourceFieldId).trim()
      : null,
    literalValue:
      binding?.literalValue == null ? null : String(binding.literalValue),
    required: binding?.required === true,
  })),
  timeoutPolicy: {
    timeoutMinutes: Number(config?.timeoutPolicy?.timeoutMinutes),
  },
});

export function validateWhatsAppFlowConfig(
  configArg,
  { revisionFields, customFields } = {}
) {
  const config = normalizeWhatsAppFlowConfig(configArg);
  if (!config.flowDefinitionId) return invalid("Select an imported WhatsApp Form.", "form");
  if (!config.flowRevisionId) return invalid("Select a published Form revision.", "form");
  if (!config.entry.buttonText || config.entry.buttonText.length > 20)
    return invalid("Button text must be between 1 and 20 characters.", "entry");
  if (/^open form$/i.test(config.entry.buttonText))
    return invalid("Use a specific action such as Share Details instead of Open Form.", "entry");
  if (config.entry.bodyText.length > 1024)
    return invalid("Message body cannot exceed 1024 characters.", "entry");

  const mappings = config.fieldMappings;
  const keys = mappings.map(mapping => mapping.responseKey);
  const ids = mappings.map(
    mapping => `${mapping.destinationKind}:${mapping.fieldId}`
  );
  if (keys.some(key => !key) || new Set(keys).size !== keys.length)
    return invalid("Each response key must be mapped exactly once.", "mappings");
  if (mappings.some(mapping => !mapping.fieldId) || new Set(ids).size !== ids.length)
    return invalid("Each response must use a unique destination field.", "mappings");

  if (Array.isArray(revisionFields)) {
    const expected = new Map(
      revisionFields.map(field => [String(field.responseKey), field])
    );
    if (
      mappings.length !== expected.size ||
      mappings.some(mapping => !expected.has(mapping.responseKey))
    )
      return invalid("Map every response field from the pinned revision.", "mappings");
    for (const mapping of mappings) {
      const field = expected.get(mapping.responseKey);
      if (
        mapping.responseType !== String(field.responseType) ||
        mapping.required !== (field.isRequired === true)
      )
        return invalid(`Response field “${mapping.responseKey}” is stale.`, "mappings");
    }
  } else if (mappings.length === 0) {
    return invalid("Map every response field from the pinned revision.", "mappings");
  }

  if (Array.isArray(customFields)) {
    const fieldById = new Map(customFields.map(field => [String(field.id), field]));
    for (const mapping of mappings) {
      const destination = fieldById.get(mapping.fieldId);
      if (!destination || destination.isActive === false)
        return invalid(`Mapped field for “${mapping.responseKey}” was deleted.`, "mappings");
      const expectedEntityType =
        mapping.destinationKind === WHATSAPP_FLOW_DESTINATION_KINDS.REQUEST_CONTEXT
          ? "request_context"
          : "contact";
      if (
        String(destination.entityType || "").trim().toLowerCase() !==
        expectedEntityType
      )
        return invalid("Mapped field has the wrong destination kind.", "mappings");
      if (!isWhatsAppFlowFieldCompatible(
        mapping.responseType,
        destination.dataType,
        mapping.destinationKind
      ))
        return invalid(`Mapped field for “${mapping.responseKey}” is no longer compatible.`, "mappings");
    }
  }

  const bindingKeys = new Set();
  for (const binding of config.initialDataBindings) {
    if (!/^[a-z0-9_.-]+$/.test(binding.dataKey) || bindingKeys.has(binding.dataKey))
      return invalid("Initial-data keys must be unique lowercase identifiers.", "initialData");
    bindingKeys.add(binding.dataKey);
    if (
      !["SessionValue", "CanonicalField", "Literal"].includes(binding.sourceKind) ||
      (binding.sourceKind === "SessionValue" && !binding.sourceFieldKey) ||
      (binding.sourceKind === "CanonicalField" && !binding.sourceFieldId) ||
      (binding.sourceKind === "Literal" &&
        (String(binding.literalValue || "").length > 512 ||
          /(access[_-]?token|authorization|app[_-]?secret|password|bearer)/i.test(
            `${binding.dataKey} ${binding.literalValue || ""}`
          )))
    )
      return invalid(`Initial-data binding “${binding.dataKey || "new binding"}” is invalid.`, "initialData");
    if (
      binding.sourceKind === "CanonicalField" &&
      Array.isArray(customFields) &&
      !customFields.some(
        field =>
          String(field.id) === binding.sourceFieldId &&
          field.isActive !== false
      )
    )
      return invalid(
        `Initial-data field for “${binding.dataKey}” was deleted.`,
        "initialData"
      );
  }

  const timeout = config.timeoutPolicy.timeoutMinutes;
  if (
    !Number.isInteger(timeout) ||
    timeout < WHATSAPP_FLOW_TIMEOUT_MINUTES ||
    timeout > WHATSAPP_FLOW_TIMEOUT_MAXIMUM_MINUTES
  )
    return invalid(
      `Timeout must be between ${WHATSAPP_FLOW_TIMEOUT_MINUTES} and ${WHATSAPP_FLOW_TIMEOUT_MAXIMUM_MINUTES} minutes.`,
      "timeout"
    );
  return { isValid: true, config };
}

const invalid = (message, section) => ({ isValid: false, message, section });
